/**
 * Shared canvas rendering utilities for seat maps.
 * Used by both the admin DesignerCanvas and the public UnifiedSeatMap
 * to ensure visual parity between builder and storefront.
 */

import { hasEdgeCurves, polygonEdgeControlPoint } from './section-geometry'

// Polyfill for ctx.roundRect() — Safari <16 and older browsers
if (typeof CanvasRenderingContext2D !== 'undefined' && !CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function (
    x: number, y: number, w: number, h: number,
    radii?: number | number[]
  ) {
    const r = typeof radii === 'number' ? radii : (Array.isArray(radii) ? radii[0] ?? 0 : 0)
    const clampedR = Math.min(r, w / 2, h / 2)
    this.moveTo(x + clampedR, y)
    this.arcTo(x + w, y, x + w, y + h, clampedR)
    this.arcTo(x + w, y + h, x, y + h, clampedR)
    this.arcTo(x, y + h, x, y, clampedR)
    this.arcTo(x, y, x + w, y, clampedR)
  }
}

export const FONT_FAMILY = 'Inter, system-ui, sans-serif'

// --- Color utilities ---

export function lightenColor(hex: string, amount: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * amount))}, ${Math.min(255, Math.round(g + (255 - g) * amount))}, ${Math.min(255, Math.round(b + (255 - b) * amount))})`
}

export function darkenColor(hex: string, amount: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgb(${Math.max(0, Math.round(r * (1 - amount)))}, ${Math.max(0, Math.round(g * (1 - amount)))}, ${Math.max(0, Math.round(b * (1 - amount)))})`
}

export function hexToRgba(hex: string, alpha: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return `rgba(128,128,128,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

export function hexToRgbaShift(hex: string, alpha: number, lighten: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return `rgba(128,128,128,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  if (lighten > 0) {
    return `rgba(${Math.min(255, Math.round(r + (255 - r) * lighten))},${Math.min(255, Math.round(g + (255 - g) * lighten))},${Math.min(255, Math.round(b + (255 - b) * lighten))},${alpha})`
  }
  const d = Math.abs(lighten)
  return `rgba(${Math.max(0, Math.round(r * (1 - d)))},${Math.max(0, Math.round(g * (1 - d)))},${Math.max(0, Math.round(b * (1 - d)))},${alpha})`
}

/**
 * Get relative luminance of a hex color (0 = black, 1 = white).
 * Used to pick contrast text color for labels inside colored seats.
 */
export function getRelativeLuminance(hex: string): number {
  if (!hex.startsWith('#') || hex.length < 7) return 0.5
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/** Returns '#ffffff' or '#1e293b' for best contrast against the given background color */
export function getContrastTextColor(bgHex: string): string {
  return getRelativeLuminance(bgHex) > 0.4 ? '#1e293b' : '#ffffff'
}

// --- Geometry utilities ---

/** Ray-casting point-in-polygon test */
export function pointInPolygon(px: number, py: number, polygon: number[][]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

/** Point-in-arc-wedge test */
export function pointInArcWedge(
  px: number, py: number,
  cx: number, cy: number,
  innerRadius: number, outerRadius: number,
  startAngle: number, endAngle: number
): boolean {
  const dx = px - cx
  const dy = py - cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist < innerRadius || dist > outerRadius) return false

  let angle = (Math.atan2(dy, dx) * 180) / Math.PI
  if (angle < 0) angle += 360

  let start = ((startAngle % 360) + 360) % 360
  let end = ((endAngle % 360) + 360) % 360

  if (start <= end) {
    return angle >= start && angle <= end
  } else {
    return angle >= start || angle <= end
  }
}

// --- Easing ---

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

// --- 3D seat sprite cache ---

const gradientSpriteCache = new Map<string, HTMLCanvasElement>()

/**
 * Generate a high-quality 3D seat sprite with off-center highlight,
 * specular dot, and inset rim shadow for a polished beveled look.
 */
export function getGradientSprite(color: string, radius: number): HTMLCanvasElement {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
  const roundedR = Math.round(radius * 2) / 2
  const key = `${color}-${roundedR}-${dpr}`
  const cached = gradientSpriteCache.get(key)
  if (cached) return cached

  const pad = 4
  const size = Math.ceil(roundedR * 2) + pad * 2
  const canvas = document.createElement('canvas')
  // Scale canvas by DPR for crisp rendering on Retina displays
  canvas.width = size * dpr
  canvas.height = size * dpr
  const c = canvas.getContext('2d')!
  c.scale(dpr, dpr)
  const cx = size / 2, cy = size / 2

  const rr = parseInt(color.slice(1, 3), 16) || 128
  const gg = parseInt(color.slice(3, 5), 16) || 128
  const bb = parseInt(color.slice(5, 7), 16) || 128

  const lighter = `rgb(${Math.min(255, rr + (255 - rr) * 0.45)}, ${Math.min(255, gg + (255 - gg) * 0.45)}, ${Math.min(255, bb + (255 - bb) * 0.45)})`
  const midLight = `rgb(${Math.min(255, rr + (255 - rr) * 0.2)}, ${Math.min(255, gg + (255 - gg) * 0.2)}, ${Math.min(255, bb + (255 - bb) * 0.2)})`
  const darker = `rgb(${Math.round(rr * 0.7)}, ${Math.round(gg * 0.7)}, ${Math.round(bb * 0.7)})`
  const rim = `rgb(${Math.round(rr * 0.6)}, ${Math.round(gg * 0.6)}, ${Math.round(bb * 0.6)})`

  // Main radial gradient — off-center highlight for 3D depth
  // More gradient stops for smoother transitions (no visible banding)
  const grad = c.createRadialGradient(
    cx - roundedR * 0.3, cy - roundedR * 0.3, roundedR * 0.08,
    cx, cy, roundedR
  )
  grad.addColorStop(0, lighter)
  grad.addColorStop(0.2, midLight)
  grad.addColorStop(0.45, color)
  grad.addColorStop(0.65, color)
  grad.addColorStop(0.85, darker)
  grad.addColorStop(0.95, rim)
  grad.addColorStop(1, rim)

  c.fillStyle = grad
  c.beginPath()
  c.arc(cx, cy, roundedR, 0, Math.PI * 2)
  c.fill()

  // Subtle bottom shadow arc (inset)
  c.globalAlpha = 0.1
  c.fillStyle = '#000000'
  c.beginPath()
  c.arc(cx, cy + roundedR * 0.08, roundedR, Math.PI * 0.25, Math.PI * 0.75)
  c.arc(cx, cy, roundedR * 0.85, Math.PI * 0.75, Math.PI * 0.25, true)
  c.closePath()
  c.fill()

  // Specular highlight dot
  c.globalAlpha = 0.3
  c.fillStyle = '#ffffff'
  c.beginPath()
  c.arc(cx - roundedR * 0.22, cy - roundedR * 0.22, roundedR * 0.18, 0, Math.PI * 2)
  c.fill()

  c.globalAlpha = 1

  if (gradientSpriteCache.size > 200) {
    // LRU eviction: remove oldest 50 entries instead of clearing everything
    const keys = Array.from(gradientSpriteCache.keys())
    for (let i = 0; i < 50 && i < keys.length; i++) gradientSpriteCache.delete(keys[i])
  }
  gradientSpriteCache.set(key, canvas)
  return canvas
}

/** Clear the gradient sprite cache (call when theme changes) */
export function clearGradientSpriteCache(): void {
  gradientSpriteCache.clear()
}

// --- Section rendering helpers ---

/**
 * Create a 3-stop linear gradient for section fills.
 * Smoother than 2-stop: subtle highlight at top, base in middle, darkened bottom.
 */
export function createSectionGradient(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, height: number,
  color: string
): CanvasGradient {
  const grad = ctx.createLinearGradient(x, y, x, y + height)
  grad.addColorStop(0, lightenColor(color, 0.20))
  grad.addColorStop(0.4, lightenColor(color, 0.05))
  grad.addColorStop(1, darkenColor(color, 0.12))
  return grad
}

/**
 * Draw edge highlights (beveled look) on a rect section.
 * Light top edge + dark bottom edge for 3D depth.
 */
export function drawEdgeHighlights(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  cornerRadius = 0
): void {
  // Top edge (light)
  ctx.globalAlpha = 0.25
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 1
  ctx.beginPath()
  if (cornerRadius > 0) {
    ctx.moveTo(x + cornerRadius, y)
    ctx.lineTo(x + w - cornerRadius, y)
  } else {
    ctx.moveTo(x, y)
    ctx.lineTo(x + w, y)
  }
  ctx.stroke()

  // Bottom edge (dark)
  ctx.globalAlpha = 0.15
  ctx.strokeStyle = '#000000'
  ctx.beginPath()
  if (cornerRadius > 0) {
    ctx.moveTo(x + cornerRadius, y + h)
    ctx.lineTo(x + w - cornerRadius, y + h)
  } else {
    ctx.moveTo(x, y + h)
    ctx.lineTo(x + w, y + h)
  }
  ctx.stroke()
  ctx.globalAlpha = 1
}

/**
 * Build a compact seat label: "Q7", "AA12", etc.
 * Returns appropriate label based on available screen space.
 */
export function getSeatLabel(
  rowLabel: string,
  seatNumber: string,
  screenRadius: number
): { text: string; fontSize: number; twoLine: boolean; topText?: string; bottomText?: string } {
  if (screenRadius >= 12) {
    // Large seats: two lines — row letter top, seat number bottom
    return {
      text: '',
      twoLine: true,
      topText: rowLabel,
      bottomText: seatNumber,
      fontSize: Math.min(screenRadius * 0.5, 8),
    }
  } else if (screenRadius >= 7) {
    // Medium seats: compact single-line "Q7"
    const compact = `${rowLabel}${seatNumber}`
    return {
      text: compact.length <= 4 ? compact : seatNumber,
      twoLine: false,
      fontSize: Math.min(screenRadius * 0.55, 7),
    }
  } else {
    // Small seats: just seat number
    return {
      text: seatNumber,
      twoLine: false,
      fontSize: Math.min(screenRadius * 0.65, 6),
    }
  }
}

// --- Shared section geometry (renderer unification) ---
// One source of truth for section outlines so the builder (DesignerCanvas) and the
// live renderer (UnifiedSeatMap) draw identical shapes/corners. Callers handle
// color/gradient/alpha; this only traces the path (beginPath..closePath).

export interface ArcConfig {
  cx: number; cy: number
  innerRadius: number; outerRadius: number
  startAngle: number; endAngle: number
}

/** Trace an arc/wedge section outline (outer arc + inner arc back). */
export function drawArcShape(ctx: CanvasRenderingContext2D, arcConfig: ArcConfig): void {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle } = arcConfig
  const startRad = (startAngle * Math.PI) / 180
  const endRad = (endAngle * Math.PI) / 180
  ctx.beginPath()
  ctx.arc(cx, cy, outerRadius, startRad, endRad, false)
  ctx.arc(cx, cy, innerRadius, endRad, startRad, true)
  ctx.closePath()
}

/**
 * Trace a polygon outline. If edgeCurves is given, each edge is bowed into a
 * quadratic curve by edgeCurves[i] (signed, perpendicular to the edge; 0 = straight) —
 * this is the drag-to-bend feature. Otherwise corners round up to maxR (0 = sharp).
 */
export function drawRoundedPolygon(ctx: CanvasRenderingContext2D, pts: number[][], maxR = 0, edgeCurves?: number[], edgeSkew?: number[]): void {
  if (pts.length < 3) return
  if (hasEdgeCurves(edgeCurves, edgeSkew)) {
    ctx.beginPath()
    ctx.moveTo(pts[0][0], pts[0][1])
    for (let i = 0; i < pts.length; i++) {
      const p0 = pts[i], p1 = pts[(i + 1) % pts.length]
      const b = edgeCurves?.[i] || 0
      const sk = edgeSkew?.[i] || 0
      if (!b && !sk) { ctx.lineTo(p1[0], p1[1]); continue }
      // Same control point the flattened outline / SVG path use (section-geometry.ts)
      const [cx, cy] = polygonEdgeControlPoint(p0, p1, b, sk)
      ctx.quadraticCurveTo(cx, cy, p1[0], p1[1])
    }
    ctx.closePath()
    return
  }
  const cornerR = Math.min(maxR, Math.min(...pts.map((p, i) => {
    const prev = pts[(i - 1 + pts.length) % pts.length]
    const next = pts[(i + 1) % pts.length]
    const d1 = Math.sqrt((p[0] - prev[0]) ** 2 + (p[1] - prev[1]) ** 2)
    const d2 = Math.sqrt((p[0] - next[0]) ** 2 + (p[1] - next[1]) ** 2)
    return Math.min(d1, d2) / 3
  })))
  ctx.beginPath()
  if (cornerR > 1) {
    for (let i = 0; i < pts.length; i++) {
      const curr = pts[i], prev = pts[(i - 1 + pts.length) % pts.length], next = pts[(i + 1) % pts.length]
      const dx1 = curr[0] - prev[0], dy1 = curr[1] - prev[1], d1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
      const dx2 = next[0] - curr[0], dy2 = next[1] - curr[1], d2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)
      const r = Math.min(cornerR, d1 / 2, d2 / 2)
      const ax = curr[0] - (dx1 / d1) * r, ay = curr[1] - (dy1 / d1) * r
      const bx = curr[0] + (dx2 / d2) * r, by = curr[1] + (dy2 / d2) * r
      if (i === 0) ctx.moveTo(ax, ay)
      else ctx.lineTo(ax, ay)
      ctx.quadraticCurveTo(curr[0], curr[1], bx, by)
    }
  } else {
    ctx.moveTo(pts[0][0], pts[0][1])
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1])
  }
  ctx.closePath()
}

export interface SectionShapeInput {
  shape?: string                 // 'rect' | 'polygon' | 'arc'
  sectionType?: string           // 'SEATED' | 'GA' | 'TABLE' | 'BOOTH'
  x: number; y: number; width: number; height: number
  points?: number[][]
  edgeCurves?: number[]              // per-edge bow for polygons
  edgeSkew?: number[]               // per-edge along-edge slide of the curve peak
  arcConfig?: ArcConfig
  tableShape?: 'round' | 'rectangle'  // for TABLE sections
}

/** The stored-section fields traceSectionPath needs (LayoutSection and DesignerSection both satisfy it). */
export interface SectionShapeSource {
  shape?: string
  sectionType?: string
  x: number; y: number; width: number; height: number
  points?: number[][]
  edgeCurves?: number[]
  edgeSkew?: number[]
  arcConfig?: ArcConfig
  tableConfig?: { tableShape?: 'round' | 'rectangle' } | null
}

/**
 * Build the traceSectionPath input from a stored section. BOTH renderers go
 * through this so a geometry field can never be forwarded by one and dropped
 * by the other (the storefront once hand-built this object without
 * edgeCurves/edgeSkew and drew every bent polygon with straight edges).
 * `sectionType` overrides the stored one (the storefront resolves it from the
 * overview data when the layout omits it).
 */
export function sectionShapeInputFrom(s: SectionShapeSource, sectionType?: string): SectionShapeInput {
  return {
    shape: s.shape,
    sectionType: sectionType ?? s.sectionType,
    x: s.x, y: s.y, width: s.width, height: s.height,
    points: s.points,
    edgeCurves: s.edgeCurves,
    edgeSkew: s.edgeSkew,
    arcConfig: s.arcConfig,
    tableShape: s.tableConfig?.tableShape,
  }
}

/**
 * Canonical section outline shared by both renderers. Issues beginPath..closePath;
 * the caller then fills and/or strokes. Corner radii here are THE source of truth:
 *   - arc            → wedge
 *   - polygon        → straight edges, or bowed per edgeCurves/edgeSkew
 *   - TABLE round    → circle, radius min(w,h)/2 - 15
 *   - TABLE rect     → roundRect inset 15, radius 8
 *   - BOOTH          → roundRect radius 12
 *   - SEATED / GA    → sharp rect
 */
export function traceSectionPath(ctx: CanvasRenderingContext2D, s: SectionShapeInput): void {
  if (s.shape === 'arc' && s.arcConfig) {
    drawArcShape(ctx, s.arcConfig)
    return
  }
  if (s.shape === 'polygon' && s.points && s.points.length >= 3) {
    drawRoundedPolygon(ctx, s.points, 0, s.edgeCurves, s.edgeSkew)
    return
  }
  if (s.sectionType === 'TABLE') {
    const tableShape = s.tableShape || 'round'
    if (tableShape === 'round') {
      const cx = s.x + s.width / 2, cy = s.y + s.height / 2
      const r = Math.min(s.width, s.height) / 2 - 15
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
    } else {
      ctx.beginPath()
      ctx.roundRect(s.x + 15, s.y + 15, s.width - 30, s.height - 30, 8)
    }
    return
  }
  if (s.sectionType === 'BOOTH') {
    ctx.beginPath()
    ctx.roundRect(s.x, s.y, s.width, s.height, 12)
    return
  }
  // SEATED / GA — sharp rectangle
  ctx.beginPath()
  ctx.rect(s.x, s.y, s.width, s.height)
}

/**
 * Compute a section's label anchor (center + rotation) from its bounds.
 * Shared so the builder and the live renderer position section names identically.
 * - rect/seated: geometric center of the bounding box
 * - polygon: centroid of the vertex list
 * Honors per-section labelOffsetX/Y nudges and labelAngle (degrees → radians).
 */
export function computeSectionLabelPosition(section: {
  x: number; y: number; width: number; height: number
  shape?: string
  points?: number[][]
  labelOffsetX?: number
  labelOffsetY?: number
  labelAngle?: number
}): { x: number; y: number; angle: number } {
  let cx: number, cy: number
  if (section.shape === 'polygon' && section.points && section.points.length >= 3) {
    cx = section.points.reduce((s, p) => s + p[0], 0) / section.points.length
    cy = section.points.reduce((s, p) => s + p[1], 0) / section.points.length
  } else {
    cx = section.x + section.width / 2
    cy = section.y + section.height / 2
  }
  return {
    x: cx + (section.labelOffsetX || 0),
    y: cy + (section.labelOffsetY || 0),
    angle: (section.labelAngle || 0) * Math.PI / 180,
  }
}

// --- Shared seat-draw primitives (Order 1) ---
// Additive, behavior-preserving wrappers so the builder (DesignerCanvas) and the
// live renderer (UnifiedSeatMap) can converge on one seat-drawing implementation.
// Defaults reproduce each renderer's CURRENT look so wiring is a visual no-op.

export interface SeatDrawOptions {
  /** Stroke a border around the seat. Builder = true, live renderer FREE = false. */
  showStroke?: boolean
  /** Explicit stroke color; defaults to a darkened version of the fill. */
  strokeColor?: string
  strokeWidth?: number
  /** Render the beveled 3D sprite instead of a flat circle. */
  showGradient?: boolean
  /** Fill/stroke alpha. */
  alpha?: number
}

/**
 * Draw a single seat as either a flat filled circle (default) or a 3D sprite.
 * Mirrors the inline seat draw in DesignerCanvas (flat + darkened border).
 */
export function drawSeat(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, r: number,
  color: string,
  opts: SeatDrawOptions = {}
): void {
  const prevAlpha = ctx.globalAlpha
  if (opts.alpha != null) ctx.globalAlpha = opts.alpha

  if (opts.showGradient && typeof document !== 'undefined') {
    const sprite = getGradientSprite(color, r)
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
    const drawSize = sprite.width / dpr
    ctx.drawImage(sprite, x - drawSize / 2, y - drawSize / 2, drawSize, drawSize)
  } else {
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
  }

  if (opts.showStroke) {
    ctx.strokeStyle = opts.strokeColor ?? darkenColor(color.startsWith('#') ? color : '#94a3b8', 0.3)
    ctx.lineWidth = opts.strokeWidth ?? 1
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.stroke()
  }

  ctx.globalAlpha = prevAlpha
}

export interface SeatBatchEntry { x: number; y: number; r: number }

/**
 * Batch-draw a map of color -> seat positions in a single path per color.
 * Faithful reproduction of UnifiedSeatMap's drawSeatBucket (flat circles).
 * When opts.showGradient is set, draws per-seat 3D sprites instead (no batching).
 */
export function drawSeatBatch(
  ctx: CanvasRenderingContext2D,
  buckets: Map<string, SeatBatchEntry[]>,
  alpha: number,
  opts: { showGradient?: boolean } = {}
): void {
  if (buckets.size === 0) return
  ctx.globalAlpha = alpha
  if (opts.showGradient && typeof document !== 'undefined') {
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
    for (const [color, seats] of buckets) {
      for (const { x, y, r } of seats) {
        const sprite = getGradientSprite(color, r)
        const drawSize = sprite.width / dpr
        ctx.drawImage(sprite, x - drawSize / 2, y - drawSize / 2, drawSize, drawSize)
      }
    }
  } else {
    for (const [color, seats] of buckets) {
      ctx.fillStyle = color
      ctx.beginPath()
      for (const { x, y, r } of seats) {
        ctx.moveTo(x + r, y)
        ctx.arc(x, y, r, 0, Math.PI * 2)
      }
      ctx.fill()
    }
  }
  ctx.globalAlpha = 1
}

/**
 * Draw a seat's row/seat label, sizing text to the on-screen radius.
 * Composes getSeatLabel(); centered, white by default for contrast on fills.
 */
export function drawSeatLabel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  screenRadius: number,
  rowLabel: string,
  seatNumber: string,
  opts: { color?: string } = {}
): void {
  const label = getSeatLabel(rowLabel, seatNumber, screenRadius)
  ctx.fillStyle = opts.color ?? '#ffffff'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `${label.fontSize}px ${FONT_FAMILY}`
  if (label.twoLine) {
    ctx.fillText(label.topText || '', x, y - label.fontSize * 0.5)
    ctx.fillText(label.bottomText || '', x, y + label.fontSize * 0.5)
  } else {
    ctx.fillText(label.text, x, y)
  }
}

/**
 * Draw a checkmark inside a seat circle (for selected state).
 */
export function drawSeatCheckmark(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, r: number
): void {
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = Math.max(0.7, r * 0.07)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(x - r * 0.18, y + r * 0.01)
  ctx.lineTo(x - r * 0.03, y + r * 0.16)
  ctx.lineTo(x + r * 0.2, y - r * 0.13)
  ctx.stroke()
  ctx.lineCap = 'butt'
  ctx.lineJoin = 'miter'
}
